import { useState, ComponentType } from "react";
import { Container, Row, Col, Button, Tab, Nav } from "react-bootstrap";
import styled from "@emotion/styled";
import { RowBox } from "components/common";
import { WidgetCategory, WidgetSubmitData, WidgetSource } from "types";
import {
    widgetTemplateCategories,
    widgetTypeCategories,
    tabOptions,
} from "./data/widget";
import { useHistory } from "react-router-dom";

// Category Menu item component
const CategoryMenuItem = styled(Button) <{ selected: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    background: ${({ selected }) =>
        selected ? "#424D65" : "transparent"}!important;
    border: none;
    border-radius: 5px;
    padding: 4px 12px;
    margin-bottom: 6px;
    gap: 18px;
    text-align: left;
    transition: background 0.2s;

    &:hover {
        background: #424d65;
    }

    .icon {
        width: 20px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .label {
        flex: 1 0 auto;
        text-align: left;
    }

    .arrow {
        display: flex;
        visibility: ${({ selected }) => (selected ? "visible" : "hidden")};
        width: 16px;
        align-items: center;
        vertical-align: middle;
        color: #1bbfae;
        font-size: 24px;
    }
`;

// Tab Navigation link component
const TabLink = styled(Nav.Link)`
    color: #ffffff !important;
    background: transparent !important;
    font-weight: 500;
    border: none !important;
    border-radius: 0;
    font-size: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-right: 2px;
    transition: color 0.2s, background 0.2s, border-bottom 0.2s;
    position: relative;
    opacity: 1 !important;

    &.active {
        color: #1bbfae;
        background: #171f30 !important;

        &::after {
            position: absolute;
            width: 100%;
            height: 4px;
            background: #00cbaf;
            top: -5px;
            left: 0;
            content: "";
            transition: all 0.5s;
        }
    }
`;

// Styled component for widget template cards
const StyledWidgetCard = styled.div<{ selected: boolean }>`
    background: ${({ selected }) => (selected ? "#424D65" : "transparent")};
    border-radius: 10px;
    border: ${({ selected }) => (selected ? "1.5px solid #424D65" : "none")};
    cursor: pointer;
    min-height: 200px;
    position: relative;
    transition: border 0.2s, background 0.2s;
    padding: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .widget-header {
        display: flex;
        align-items: center;
        width: 100%;
        margin-bottom: 8px;
    }
    .widget-header-content {
        display: flex;
        flex-direction: column;
        margin-right: 8px;
        width: calc(100% - 32px);

        div.widget-title {
            font-weight: 400;
            font-size: 14px;
            margin-bottom: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
    .widget-description {
        color: #828fab;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.5;
        word-break: break-word;
        white-space: normal;
    }
    .widget-radio {
        width: 21px;
        height: 21px;
        border-radius: 50%;
        /* border: ${({ selected }) =>
        selected ? "none" : "2px solid #BFC9DA"}; */
        background: ${({ selected }) => (selected ? "#1BBFAE" : "#828FAB")};
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: flex-start;
        margin-top: 8px;
        span {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #424d65;
        }
    }
    .widget-preview {
        width: 100%;
        height: auto;
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        svg {
            border-radius: 4px;
            object-fit: cover;
            background: transparent;
            width: 100%;
            height: 100%;
        }
    }
`;

const normalizeImagePreview = (
    ImagePreview: WidgetCardProps["ImagePreview"]
): {
    src: string | null;
    Component: ComponentType<React.SVGProps<SVGSVGElement>> | null;
} => {
    // Case 1: Direct string path
    if (typeof ImagePreview === "string") {
        return { src: ImagePreview, Component: null };
    }

    // Case 2: Object with default property (ES6 module)
    if (
        ImagePreview &&
        typeof ImagePreview === "object" &&
        "default" in ImagePreview
    ) {
        return { src: ImagePreview.default, Component: null };
    }

    // Case 3: React component (SVG)
    if (typeof ImagePreview === "function") {
        return { src: null, Component: ImagePreview };
    }

    return { src: null, Component: null };
};

type WidgetCardProps = {
    selected: boolean;
    onClick: () => void;
    name: string;
    description: string;
    ImagePreview:
    | string
    | ComponentType<React.SVGProps<SVGSVGElement>>
    | { default: string };
};
// WidgetCard component for widget template cards (now uses StyledWidgetCard)
const WidgetCard = ({
    selected,
    onClick,
    name,
    description,
    ImagePreview,
}: WidgetCardProps) => {
    const { src, Component } = normalizeImagePreview(ImagePreview);

    const renderPreview = () => {
        if (src) {
            return (
                <img
                    src={src}
                    alt={name}
                    style={{ width: "100%", objectFit: "cover" }}
                />
            );
        }
        if (Component) {
            return <Component />;
        }
        return null;
    };

    return (
        <StyledWidgetCard selected={selected} onClick={onClick}>
            <div className="widget-header">
                <div className="widget-header-content">
                    <div className="widget-title">{name}</div>
                    <div className="widget-description">{description}</div>
                </div>
                {/* Radio selector */}
                <span className="widget-radio">{selected && <span />}</span>
            </div>
            <div className="widget-preview">
                {/* Widget preview image (full card preview, not icon) */}
                {renderPreview()}
            </div>
        </StyledWidgetCard>
    );
};

// Widget Template Panel (used for both templates and types)
// Accept Icon as React.ComponentType<any> for compatibility with both SVG and test mocks
type WidgetPanelProps = {
    categories: Array<WidgetCategory>;
    onSubmit: (data: WidgetSubmitData) => void;
};
const WidgetPanel = ({ categories, onSubmit }: WidgetPanelProps) => {
    const history = useHistory();
    const [selectedWidget, setSelectedWidget] = useState<number | null>(
        categories[0]?.widgets?.[0]?.id ?? null
    );
    const [selectedCategory, setSelectedCategory] = useState<number>(0);

    const handleSelectCategory = (idx: number) => {
        setSelectedCategory(idx);
        setSelectedWidget(categories[idx].widgets?.[0]?.id ?? null);
    };

    const handleSelectWidget = (id: number) => setSelectedWidget(id);

    const handleSubmit = () => {
        const selectedTemplate = categories[selectedCategory].widgets.find(
            (widget) => widget.id === selectedWidget
        );

        if (!selectedTemplate) {
            return;
        }

        onSubmit({
            widgetSource: categories[selectedCategory].id as WidgetSource,
            widgetTemplate: selectedTemplate,
        });
    };

    return (
        <Row>
            <Col xs={12} md={5} xl={3}>
                <RowBox className="flex-column" borderRadius={"10px"}>
                    <div className="font-weight-normal text-white mb-4">
                        Widget templates
                    </div>
                    {categories.map((cat, idx) => (
                        <CategoryMenuItem
                            key={cat.label}
                            selected={selectedCategory === idx}
                            onClick={() => handleSelectCategory(idx)}
                        >
                            <span className="icon">
                                <cat.Icon width={16} height={16} />
                            </span>
                            <span className="label">{cat.label}</span>
                            <span className="arrow">&#8250;</span>
                        </CategoryMenuItem>
                    ))}
                </RowBox>
            </Col>
            <Col>
                <RowBox
                    className="flex-column templates-list"
                    borderRadius={"10px"}
                >
                    <div className="font-weight-normal text-white mb-4">
                        {categories[selectedCategory].label}
                    </div>
                    <Row xs={1} md={2} xl={3} className="g-4">
                        {categories[selectedCategory].widgets.map(
                            (template) => (
                                <Col key={template.id}>
                                    <WidgetCard
                                        selected={
                                            selectedWidget === template.id
                                        }
                                        onClick={() =>
                                            handleSelectWidget(template.id)
                                        }
                                        name={template.name}
                                        description={template.description}
                                        ImagePreview={template.ImagePreview}
                                    />
                                </Col>
                            )
                        )}
                    </Row>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 32,
                            gap: 16,
                            verticalAlign: "bottom",
                        }}
                    >
                        <Button
                            variant="outline-light"
                            style={{ minWidth: 120 }}
                            onClick={() => {
                                history.push("/dashboard");
                            }}
                        >
                            CANCEL
                        </Button>
                        <Button
                            disabled={!selectedWidget || selectedWidget < 1}
                            variant="primary"
                            style={{ minWidth: 120 }}
                            onClick={handleSubmit}
                        >
                            NEXT
                        </Button>
                    </div>
                </RowBox>
            </Col>
        </Row>
    );
};

type CreateWidgetProps = {
    onSubmit: (data: WidgetSubmitData) => void;
    onCancel?: () => void;
    templateCategories?: WidgetCategory[];
    typeCategories?: WidgetCategory[];
};
// Main component
const CreateWidget = ({
    onSubmit,
    onCancel,
    templateCategories = widgetTemplateCategories,
    typeCategories = widgetTypeCategories,
}: CreateWidgetProps) => {
    const [activeTab, setActiveTab] = useState<string>("templates");

    return (
        <Container fluid className="py-3" style={{ minHeight: "100vh" }}>
            <Row>
                <Col sm={12}>
                    <h5 className="page-title mb-4 font-22">Add Widget</h5>
                </Col>
            </Row>
            <div>
                <Tab.Container
                    id="create-widget-tabs"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k as string)}
                >
                    <Row>
                        <Col sm={12}>
                            <Nav
                                variant="tabs"
                                className="mb-4 px-lg-5 border-bottom-0 bg-secondary"
                            >
                                {tabOptions.map((tab) => (
                                    <Nav.Item key={tab.key}>
                                        <TabLink
                                            className="px-sm-5 py-md-3"
                                            eventKey={tab.key}
                                        >
                                            {tab.label}
                                        </TabLink>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        </Col>
                        <Col sm={12}>
                            <Tab.Content>
                                <Tab.Pane eventKey="templates">
                                    {activeTab === "templates" && (
                                        <WidgetPanel
                                            categories={templateCategories}
                                            onSubmit={onSubmit}
                                        />
                                    )}
                                </Tab.Pane>
                                {/* <Tab.Pane eventKey="types">
                                    {activeTab === "types" && (
                                        <WidgetPanel
                                            categories={typeCategories}
                                            onSubmit={onSubmit}
                                        />
                                    )}
                                </Tab.Pane> */}
                            </Tab.Content>
                        </Col>
                    </Row>
                </Tab.Container>
            </div>
        </Container>
    );
};

export default CreateWidget;
